import './Chat.scss';

import { Divider, Flex } from 'antd';
import React, { useCallback, useEffect, useState } from 'react'
import { ChatMessage } from '../../models/LLM';
import { Col, Row } from 'react-bootstrap';
import MessageCloud from './MessageCloud';
import TypingLoader from './TypingLoader';
import ChatInput from './ChatInput';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';

type Props = {
  className?: string;
  history?: ChatMessage[];
  loading?: boolean;
  extra?: React.ReactNode;
  emptyChatIcon?: React.ReactNode;
  emptyChatMessage?: React.ReactNode;
  actions?: React.ReactNode;
  error?: string;
  onMessageSend?: (message: ChatMessage) => void;
  onMessageStop?: () => void;
  disabledInput?: boolean;
  selectedAgentName?: string;
}

export default function Chat({ history = [], loading = false, error, className, extra, emptyChatIcon, emptyChatMessage, actions, onMessageSend, disabledInput, selectedAgentName, onMessageStop }: Props) {
  const [prompt, setPrompt] = useState("");

  const scrollChatToBottom = useCallback(() => {
    const chatContainer = document.getElementById('chat-history-container');
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, []);

  useEffect(scrollChatToBottom, [history, scrollChatToBottom]);

  const handleSendMessage = useCallback(() => {
    if (!prompt.trim()) return;
    const message: ChatMessage = {
      timestamp: new Date().toISOString(),
      text: prompt,
      agentName: selectedAgentName,
      role: "user",
    };
    // setHistory(h => h!.concat(message));
    setPrompt('');
    onMessageSend?.(message);
  }, [prompt, onMessageSend, selectedAgentName]);

  return (
    <div className={classNames("chat-container", className)}>
      {extra}

      <div className="chat-history" id="chat-history-container">
        {history.length === 0 && !error && (
          <Flex justify="center" className="py-5">
            <div className="chat-alert empty">
              {emptyChatIcon || (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={0.75} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                </svg>
              )}

              <p className="text-center mt-3">
                {emptyChatMessage || 'Welcome to your brand new chat, start exchanging messages now!'}
              </p>
            </div>
          </Flex>
        )}

        {error && (
          <Flex justify="center" className="py-5">
            <div className="chat-alert error">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>

              <p className="text-center mt-3">{error}</p>
            </div>
          </Flex>
        )}

        {history.map(message => (
          message.isDivider
            ? (
              <Divider key={message.timestamp} plain className="chat-divider">
                <div className="divider-pill">
                  <FontAwesomeIcon icon={solid('robot')} size="sm" />
                  <span className="text-truncate ms-2">{message.text}</span>
                </div>
              </Divider>
            )
            : (
              <Row className={classNames("chat-message-row", message.role, message.kind)} key={message.timestamp}>
                <MessageCloud message={message} />
              </Row>
            )
        ))}

        {loading && (
          <Row className="mx-0 py-1">
            <MessageCloud>
              <TypingLoader />
            </MessageCloud>
          </Row>
        )}
      </div>

      <Row className="chat-toolbar mx-0">
        <Col>
          <ChatInput
            prompt={prompt}
            onPromptChange={setPrompt}
            onSendMessage={handleSendMessage}
            onStopMessage={onMessageStop}
            disabled={!!error || disabledInput}
            loading={loading}
          />
          <input className="d-none" type="file" id="file-upload-input" />
          <div className="text-center">
            <small className="text-muted font-sm">AI assistants can make mistakes, ensure to verify information.</small>
          </div>
        </Col>
        {actions && (
          <Col className="buttons-col">
            {actions}
          </Col>
        )}
      </Row>
    </div>
  )
}